/**
 * A cara da barbearia numa marca só.
 *
 * Antes daqui, toda tela sem logo mostrava as duas primeiras letras do nome —
 * "BA" para "Barbearia do Zé", que não quer dizer nada para ninguém e parece um
 * app inacabado logo na primeira tela que o cliente vê.
 *
 * A tesoura do favicon resolve melhor: é uma barbearia, todo mundo entende, e
 * ela se pinta com a cor que o dono escolheu. Quando a logo existir, ela ganha
 * — mas o estado sem logo deixa de ser um buraco.
 */

import { useTenant } from '../lib/contexts.tsx';

interface Props {
  size?: number;
  /** Quadrado com fundo (home, login) ou só o traço (cabeçalho). */
  framed?: boolean;
  className?: string;
}

export function TenantMark({ size = 80, framed = true, className }: Props) {
  const { tenant } = useTenant();

  if (tenant.logoUrl) {
    return (
      <img
        src={tenant.logoUrl}
        alt={tenant.name}
        width={size}
        height={size}
        className={`rounded-2xl object-cover ${className ?? ''}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center ${
        framed ? 'rounded-2xl bg-primary/12' : ''
      } ${className ?? ''}`}
      style={{ width: size, height: size }}
    >
      <Scissors size={Math.round(size * (framed ? 0.6 : 1))} />
    </span>
  );
}

/**
 * A mesma tesoura do `public/icone.svg`, inline para herdar a cor do tema.
 *
 * Duplicar o desenho é de propósito: o arquivo em `public/` é o que o navegador
 * pede como favicon e ícone do PWA, e precisa existir como arquivo estático;
 * aqui ele precisa ser SVG no DOM para `currentColor` funcionar. Um `<img>`
 * apontando para o arquivo não aceita mudar de cor.
 */
export function Scissors({ size = 48 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      fill="none"
      stroke="currentColor"
      strokeWidth={30}
      strokeLinecap="round"
      className="text-primary"
      role="img"
      aria-label="Barbearia"
    >
      <path d="M168 132 L330 330" />
      <path d="M344 132 L182 330" />
      <circle cx="166" cy="380" r="44" />
      <circle cx="346" cy="380" r="44" />
    </svg>
  );
}
